import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { 
  ChatMessageResponse, 
  ConversationResponse, 
  UserPresencePayload, 
  TypingNotificationPayload, 
  ReadReceiptPayload 
} from '../../models/chat/chat-api.models';

@Injectable({
  providedIn: 'root'
})
export class ChatSignalRService {
  private hubConnection!: signalR.HubConnection;
  
  // Connection State
  public connectionState$ = new BehaviorSubject<signalR.HubConnectionState>(signalR.HubConnectionState.Disconnected);

  // Events
  public messageReceived = new Subject<ChatMessageResponse>();
  public conversationUpdated = new Subject<ConversationResponse>();
  public userPresence = new Subject<UserPresencePayload>();
  
  // Combined typing subject for easier consumption
  public userTyping = new Subject<TypingNotificationPayload & { isTyping: boolean }>();
  
  public messagesRead = new Subject<ReadReceiptPayload>();
  public messagePinnedUpdated = new Subject<ChatMessageResponse>();

  constructor() { }

  public startConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(environment.apis.default.baseUrl + '/hubs/realtime', {
          accessTokenFactory: () => {
             const account = localStorage.getItem('account');
             return account ? JSON.parse(account).token : '';
          }
        })
        .withAutomaticReconnect()
        .build();

      // Monitor State
      this.hubConnection.onreconnecting(() => this.connectionState$.next(signalR.HubConnectionState.Reconnecting));
      this.hubConnection.onreconnected(() => this.connectionState$.next(signalR.HubConnectionState.Connected));
      this.hubConnection.onclose(() => this.connectionState$.next(signalR.HubConnectionState.Disconnected));

      this.hubConnection.start()
        .then(() => {
          console.log('Chat Hub Connection started');
          this.connectionState$.next(signalR.HubConnectionState.Connected);
          this.registerSignalREvents();
          resolve();
        })
        .catch(err => {
          console.error('Error while starting Chat Hub connection: ' + err);
          this.connectionState$.next(signalR.HubConnectionState.Disconnected);
          reject(err);
        });
    });
  }

  private registerSignalREvents() {
    // 1. Message Received
    this.hubConnection.on('ReceiveMessage', (data: ChatMessageResponse) => {
      this.messageReceived.next(data);
    });

    // 2. Conversation Updated
    this.hubConnection.on('ConversationUpdated', (data: ConversationResponse) => {
      this.conversationUpdated.next(data);
    });

    // 3. Presence
    this.hubConnection.on('UserPresenceChanged', (userId: string, presenceStatus: number, lastSeen: string | null) => {
        this.userPresence.next({ userId, presenceStatus, lastSeen });
    });

    // 4. Typing
    this.hubConnection.on('UserTyping', (conversationId: string, userId: string) => {
         this.userTyping.next({ conversationId, userId, isTyping: true });
    });

    this.hubConnection.on('UserStoppedTyping', (conversationId: string, userId: string) => {
         this.userTyping.next({ conversationId, userId, isTyping: false });
    });
    
    // 5. Read Receipts
    this.hubConnection.on('MessagesRead', (data: ReadReceiptPayload) => {
        this.messagesRead.next(data);
    });

    // 6. Pinned Message
    this.hubConnection.on('MessagePinnedUpdated', (data: ChatMessageResponse) => {
        this.messagePinnedUpdated.next(data);
    });
  }

  // --- Invoke Methods ---

  public sendTyping(conversationId: number) {
      if (this.hubConnection?.state === signalR.HubConnectionState.Connected)
          this.hubConnection.invoke('SendTyping', conversationId.toString());
  }

  public sendStoppedTyping(conversationId: number) {
      if (this.hubConnection?.state === signalR.HubConnectionState.Connected)
          this.hubConnection.invoke('SendStoppedTyping', conversationId.toString());
  }

  public joinConversation(conversationId: number) {
      if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
          this.hubConnection.invoke('JoinGroup', `Conversation-${conversationId}`)
              .catch(err => console.error('JoinGroup Error:', err));
      }
  }

  public leaveConversation(conversationId: number) {
      if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
          this.hubConnection.invoke('LeaveGroup', `Conversation-${conversationId}`)
              .catch(err => console.error('LeaveGroup Error:', err));
      }
  }

  public stopConnection() {
    if (this.hubConnection) {
      this.hubConnection.stop();
    }
  }

  public async getUsersPresence(userIds: string[]): Promise<{ userId: string; status: number; lastSeen: string | null }[]> {
      if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
          try {
              return await this.hubConnection.invoke('GetUsersPresence', userIds);
          } catch (err) {
              console.error('Error getting users presence:', err);
              return [];
          }
      }
      return [];
  }
}
